import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { useFaq } from "@/lib/faq/useFaq";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollPanel } from "@/components/ui/scroll-panel";

export default function HelpPanel({ open, onClose }) {
  const { t } = useTranslation("help");
  const navigate = useNavigate();
  const { getHighlightedQuestions, search } = useFaq();
  const [query, setQuery] = useState("");

  const trimmed = query.trim();
  const results = trimmed ? search(trimmed) : getHighlightedQuestions();

  const handleSeeFullHelp = () => {
    onClose();
    navigate("/aide");
  };

  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <SheetContent side="right" className="flex flex-col" data-testid="help-panel">
        <SheetHeader>
          <SheetTitle>{t("ui.panel_title")}</SheetTitle>
        </SheetHeader>

        <div className="relative mt-4 mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("ui.search_placeholder")}
            data-testid="help-panel-search-input"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-qenva-blue/30"
          />
        </div>

        <ScrollPanel>
          {trimmed && results.length === 0 ? (
            <p className="text-sm text-zinc-500" data-testid="help-panel-no-results">
              {t("ui.no_results", { query: trimmed })}
            </p>
          ) : (
            <ul className="space-y-4" data-testid="help-panel-questions">
              {results.map((r) => (
                <li key={r.id}>
                  <p className="font-medium text-qenva-navy text-sm mb-1">{r.q}</p>
                  <p className="text-xs text-zinc-600">{r.a}</p>
                </li>
              ))}
            </ul>
          )}
        </ScrollPanel>

        <button
          type="button"
          onClick={handleSeeFullHelp}
          data-testid="help-panel-see-full-help"
          className="mt-6 w-full text-sm font-medium text-qenva-blue hover:underline text-left shrink-0"
        >
          {t("ui.see_full_help")}
        </button>
      </SheetContent>
    </Sheet>
  );
}
